import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DisconnectReason, Room } from 'livekit-client';
import { toast } from 'sonner';
import { getLiveKitToken } from '@/lib/firebase/livekit';
import {
  CONNECT_TIMEOUT_MS,
  LK_DISCONNECT,
  callErrorMessage,
  callOutcome,
  disconnectReasonName,
  type CallOutcome,
} from '@/lib/clinic/callSession';
import { startCallAttempt, type CallAttemptRecorder, type CallAttemptStage } from '@/lib/clinic/callAttempts';

/**
 * Token fetch + LiveKit connection lifecycle shared by FloatingVideoCall and
 * LiveKitVideoCall (#47): connect timeout, error state with retry, LiveKit
 * connection errors / disconnect reasons, "did the other party join", and the
 * `callAttempts` log.
 */

interface UseLiveKitConnectionOptions {
  enabled: boolean;
  /** LiveKit room name (already prefixed, see toLiveKitRoomName). */
  roomName: string;
  participantName: string;
  metadata: string;
  language: string;
  logPrefix: string;
  /** When given, each attempt is logged to `callAttempts`. */
  attempt?: { coachId: string; customerId: string; role: 'coach' | 'customer' } | null;
  /** Called once when the other party first joins the room. */
  onRemoteJoined?: () => void;
}

function isMediaError(err: unknown): boolean {
  const name = (err as { name?: string })?.name || '';
  return /NotAllowed|NotFound|NotReadable|Overconstrained|Permission|DeviceInUse|AbortError/i.test(name);
}

export function useLiveKitConnection({
  enabled,
  roomName,
  participantName,
  metadata,
  language,
  logPrefix,
  attempt,
  onRemoteJoined,
}: UseLiveKitConnectionOptions) {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attemptNo, setAttemptNo] = useState(0);
  const [remoteJoinedAt, setRemoteJoinedAt] = useState<number | null>(null);

  const languageRef = useRef(language);
  languageRef.current = language;
  const attemptRef = useRef(attempt);
  attemptRef.current = attempt;
  const onRemoteJoinedRef = useRef(onRemoteJoined);
  onRemoteJoinedRef.current = onRemoteJoined;

  const recorderRef = useRef<CallAttemptRecorder | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roomRef = useRef<Room | null>(null);
  const roomConnectedRef = useRef(false);
  const remoteJoinedAtRef = useRef<number | null>(null);
  const failedRef = useRef(false);
  const endedRef = useRef(false);

  const clearConnectTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fail = useCallback((stage: CallAttemptStage, message: string, code: string) => {
    if (failedRef.current || endedRef.current) return;
    failedRef.current = true;
    clearConnectTimeout();
    console.error(`${logPrefix} Call failed at ${stage}:`, code);
    recorderRef.current?.record(stage, code);
    setError(message);
    // Dropping the token unmounts <LiveKitRoom>, which disconnects the room.
    setToken('');
    setLoading(false);
  }, [logPrefix, clearConnectTimeout]);

  // One attempt: fetch a token, then give the room CONNECT_TIMEOUT_MS to connect.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    failedRef.current = false;
    endedRef.current = false;
    roomConnectedRef.current = false;
    remoteJoinedAtRef.current = null;
    setRemoteJoinedAt(null);
    setError(null);
    setToken('');
    setLoading(true);

    const ctx = attemptRef.current;
    recorderRef.current = ctx && ctx.coachId && ctx.customerId
      ? startCallAttempt({ ...ctx, roomId: roomName })
      : null;
    recorderRef.current?.record('token');

    const run = async () => {
      try {
        if (!roomName) throw new Error('Room ID is required');
        console.log(`${logPrefix} Fetching LiveKit token for room:`, roomName, 'participant:', participantName);
        const tkn = await getLiveKitToken(roomName, participantName, metadata);
        if (cancelled) return;
        console.log(`${logPrefix} Token received successfully`);
        recorderRef.current?.record('connect');
        setToken(tkn);
        setLoading(false);
        timeoutRef.current = setTimeout(() => {
          if (!roomConnectedRef.current) {
            fail('connect', callErrorMessage('timeout', languageRef.current), 'timeout');
          }
        }, CONNECT_TIMEOUT_MS);
      } catch (err) {
        if (cancelled) return;
        console.error(`${logPrefix} Error fetching token:`, err);
        fail('token', callErrorMessage('token', languageRef.current), (err as Error)?.message || 'token-failed');
      }
    };
    run();

    return () => {
      cancelled = true;
      clearConnectTimeout();
    };
  }, [enabled, roomName, participantName, metadata, attemptNo, logPrefix, fail, clearConnectTimeout]);

  /** «إعادة المحاولة»: disconnect whatever is left of the failed room, then re-fetch the token. */
  const retry = useCallback(async () => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      try {
        await room.disconnect();
      } catch (err) {
        console.warn(`${logPrefix} Disconnect before retry failed:`, err);
      }
    }
    setAttemptNo((n) => n + 1);
  }, [logPrefix]);

  /** The call is being closed by the user. Returns whether it ever really connected. */
  const finish = useCallback((): CallOutcome => {
    const outcome = callOutcome(remoteJoinedAtRef.current, Date.now());
    if (!endedRef.current && !failedRef.current) {
      recorderRef.current?.record('ended', outcome.connected ? null : 'closed-before-other-party-joined');
    }
    endedRef.current = true;
    clearConnectTimeout();
    return outcome;
  }, [clearConnectTimeout]);

  const markRemoteJoined = useCallback(() => {
    if (remoteJoinedAtRef.current != null) return;
    const now = Date.now();
    remoteJoinedAtRef.current = now;
    setRemoteJoinedAt(now);
    recorderRef.current?.record('connected');
    onRemoteJoinedRef.current?.();
  }, []);

  // Stable identities matter: <LiveKitRoom> re-runs room.connect() when
  // onError changes, so a new object per render would reconnect every render.
  const roomHandlers = useMemo(() => ({
    onConnected: () => {
      roomConnectedRef.current = true;
      clearConnectTimeout();
      console.log(`${logPrefix} Room connected`);
    },
    onDisconnected: (reason?: DisconnectReason) => {
      // We closed it ourselves (end call, failure teardown, retry).
      if (endedRef.current || failedRef.current) return;
      if ((reason as number | undefined) === LK_DISCONNECT.CLIENT_INITIATED) return;
      const code = `disconnected:${disconnectReasonName(reason as number | undefined)}`;
      fail(
        remoteJoinedAtRef.current != null ? 'ended' : 'connect',
        callErrorMessage('disconnected', languageRef.current, reason as number | undefined),
        code
      );
    },
    onError: (err: Error) => {
      if (isMediaError(err)) {
        console.warn(`${logPrefix} Media device error:`, err);
        toast.error(languageRef.current === 'ar'
          ? 'تعذّر تشغيل الكاميرا أو الميكروفون. تحقق من الأذونات.'
          : 'Could not start the camera or microphone. Check permissions.');
        return;
      }
      if (roomConnectedRef.current) {
        console.warn(`${logPrefix} LiveKit error after connect:`, err);
        return;
      }
      fail('connect', callErrorMessage('connect', languageRef.current), err?.message || 'connect-failed');
    },
  }), [fail, logPrefix, clearConnectTimeout]);

  const registerRoom = useCallback((room: Room) => {
    roomRef.current = room;
  }, []);

  return {
    token,
    loading,
    error,
    retry,
    finish,
    remoteJoinedAt,
    roomHandlers,
    /** Changes per attempt; use as the <LiveKitRoom> key so a retry gets a fresh Room. */
    attemptKey: attemptNo,
    watcherProps: { onRoom: registerRoom, onRemoteJoined: markRemoteJoined }
  };
}
